/**
 * CertificateCanvasEditor — free-form drag/drop/resize certificate designer.
 *
 * Elements schema (stored in config.elements[]):
 *   { id, type, x, y, w, h, z,
 *     // text
 *     text?, font_family?, font_size?, font_weight?, font_style?, color?, align?,
 *     // image / signature / seal / logo
 *     url?, opacity?, border_enabled?, border_width?, border_color?,
 *     // shape (rect)
 *     fill_color?, border_style?,
 *     // line
 *     thickness?, line_color?,
 *     // placeholder chip
 *     placeholder_key?,
 *     // qr
 *     size?
 *   }
 *
 * Canvas is A4 landscape @ 96 DPI: 1122 × 794 CSS px. All positions/sizes
 * are stored in these canvas pixels; the backend converts to mm at render.
 */
import { useState, useRef, useEffect, useCallback } from "react";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { Textarea } from "./ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "./ui/select";
import { Slider } from "./ui/slider";
import {
  TextT, Image as ImageIcon, Square, LineSegment, Signature as SignatureIcon,
  Seal as SealIcon, QrCode, Plus, Trash, ArrowsOutSimple, FloppyDisk,
  CheckCircle, UploadSimple, ArrowClockwise, ArrowUUpLeft, ArrowUUpRight,
  Sparkle, Eye, EyeSlash, ArrowsOut, X,
} from "@phosphor-icons/react";
import { toast } from "sonner";
import api from "../lib/api";
import { DECORATION_ITEMS, svgToDataUrl } from "./CertificateDecorations";
import { CANVAS_TEMPLATES } from "./CertificateSampleTemplates";

export const CANVAS_W_LANDSCAPE = 1122;
export const CANVAS_H_LANDSCAPE = 794;
export const CANVAS_W_PORTRAIT = 794;
export const CANVAS_H_PORTRAIT = 1122;

export const AVAILABLE_FONTS = [
  { name: "Helvetica", css: "'Helvetica', 'Arial', sans-serif" },
  { name: "Times", css: "'Times New Roman', 'Times', serif" },
  { name: "Montserrat", css: "'Montserrat', sans-serif" },
  { name: "Open Sans", css: "'Open Sans', sans-serif" },
  { name: "Lato", css: "'Lato', sans-serif" },
  { name: "Roboto", css: "'Roboto', sans-serif" },
  { name: "Poppins", css: "'Poppins', sans-serif" },
  { name: "Playfair Display", css: "'Playfair Display', serif" },
  { name: "Merriweather", css: "'Merriweather', serif" },
  { name: "Cormorant Garamond", css: "'Cormorant Garamond', serif" },
  { name: "Great Vibes", css: "'Great Vibes', cursive" },
  { name: "Dancing Script", css: "'Dancing Script', cursive" },
];

export const PLACEHOLDER_KEYS = [
  "StudentName", "ProgrammeName", "CompletionDate", "CertificateNumber",
  "InstructorName", "Classification", "Attendance", "CourseName",
];

// Round 54 — Full dynamic placeholder catalogue. These snake_case tokens
// are resolved by the backend at PDF generation time using the student's
// live enrollment data. The Canvas editor lets admins insert any of these
// into a text element via the "Insert Dynamic Field" dropdown; the token
// is preserved verbatim in the saved design and never flattened to static
// text. The optional ``sample`` value is only used for the on-screen
// preview so the admin can visualise the final certificate.
export const DYNAMIC_FIELDS = [
  { key: "student_name",       label: "Student Name",        sample: "John Adewale" },
  { key: "admission_number",   label: "Admission Number",    sample: "SBL-P01-0001" },
  { key: "program_name",       label: "Programme Name",      sample: "Foundations of School Leadership" },
  { key: "course_name",        label: "Course Name",         sample: "Curriculum Design" },
  { key: "certificate_title",  label: "Certificate Title",   sample: "Certificate of Completion" },
  { key: "certificate_number", label: "Certificate Number",  sample: "SBL-2026-A1B2C3D4" },
  { key: "date_of_completion", label: "Date of Completion",  sample: "March 15, 2026" },
  { key: "date_issued",        label: "Date Issued",         sample: "March 15, 2026" },
  { key: "start_date",         label: "Start Date",          sample: "January 10, 2026" },
  { key: "end_date",           label: "End Date",            sample: "March 10, 2026" },
  { key: "institution_name",   label: "Institution Name",    sample: "School Builder Lab Africa" },
  { key: "instructor_name",    label: "Instructor Name",     sample: "Dr. Olusegun Akintade" },
  { key: "duration",           label: "Duration",            sample: "12 weeks" },
  { key: "grade",              label: "Grade / Score",       sample: "Pass" },
  { key: "cohort",             label: "Cohort",              sample: "Cohort 3" },
  { key: "verification_url",   label: "Verification URL",    sample: "https://schoolbuilderlab.net.ng/verify/SBL-2026-SAMPLE" },
];

// Build a lookup of sample values for the on-canvas preview. Also covers
// the legacy PascalCase placeholder keys so old templates keep rendering.
export const DYNAMIC_SAMPLES = DYNAMIC_FIELDS.reduce((acc, f) => {
  acc[f.key] = f.sample;
  return acc;
}, {
  // legacy PascalCase aliases used by classic placeholder-chip elements
  StudentName: "John Adewale",
  ProgrammeName: "Foundations of School Leadership",
  CourseName: "Curriculum Design",
  CompletionDate: "March 15, 2026",
  CertificateNumber: "SBL-2026-A1B2C3D4",
  Classification: "Pass",
  InstructorName: "Dr. Olusegun Akintade",
  Attendance: "95%",
});

const uid = () => `el_${Math.random().toString(36).slice(2, 10)}`;
const clamp = (v, lo, hi) => Math.max(lo, Math.min(hi, v));

// Ensure Google Fonts are loaded once for the preview.
function loadGoogleFontsOnce() {
  if (typeof document === "undefined") return;
  if (document.getElementById("cert-editor-google-fonts")) return;
  const link = document.createElement("link");
  link.id = "cert-editor-google-fonts";
  link.rel = "stylesheet";
  link.href = "https://fonts.googleapis.com/css2?" + [
    "family=Montserrat:wght@400;600;700",
    "family=Open+Sans:wght@400;600;700",
    "family=Lato:wght@400;700",
    "family=Roboto:wght@400;500;700",
    "family=Poppins:wght@400;600;700",
    "family=Playfair+Display:wght@400;700",
    "family=Merriweather:wght@400;700",
    "family=Cormorant+Garamond:wght@400;700",
    "family=Great+Vibes",
    "family=Dancing+Script:wght@400;700",
  ].join("&") + "&display=swap";
  document.head.appendChild(link);
}

// ── Element factories ──────────────────────────────────────────────
function newTextEl(x = 400, y = 200) {
  return {
    id: uid(), type: "text", x, y, w: 500, h: 60, z: 1,
    text: "Certificate Text", font_family: "Playfair Display", font_size: 40,
    font_weight: "bold", font_style: "normal", color: "#0c1b3a", align: "center",
  };
}
function newImageEl(url = "", x = 60, y = 60) {
  return {
    id: uid(), type: "image", x, y, w: 200, h: 100, z: 1,
    url, opacity: 1, border_enabled: false, border_width: 2, border_color: "#c9a227",
  };
}
function newSignatureEl(x = 200, y = 620) {
  return { ...newImageEl("", x, y), type: "signature", w: 240, h: 100 };
}
function newSealEl(x = 820, y = 580) {
  return { ...newImageEl("", x, y), type: "seal", w: 140, h: 140, opacity: 0.9 };
}
function newLogoEl(x = 500, y = 40) {
  return { ...newImageEl("", x, y), type: "logo", w: 120, h: 120 };
}
function newShapeEl(x = 100, y = 100) {
  return {
    id: uid(), type: "shape", x, y, w: 260, h: 160, z: 0,
    fill_color: "#ffffff", opacity: 1,
    border_enabled: true, border_width: 2, border_color: "#c9a227", border_style: "solid",
  };
}
function newLineEl(x = 200, y = 400) {
  return { id: uid(), type: "line", x, y, w: 300, h: 3, z: 1, line_color: "#c9a227", thickness: 3 };
}
function newPlaceholderEl(placeholder_key = "StudentName", x = 300, y = 340) {
  return {
    id: uid(), type: "placeholder", placeholder_key, x, y, w: 520, h: 70, z: 2,
    font_family: "Great Vibes", font_size: 56, font_weight: "normal", font_style: "normal",
    color: "#0c1b3a", align: "center",
  };
}
function newQrEl(x = 40, y = 600) {
  return { id: uid(), type: "qr", x, y, w: 120, h: 120, z: 1 };
}

// Resolve inline {{token}} references in text content. When ``sample`` is
// provided (preview mode), every known key becomes its sample value; when
// null, tokens render verbatim so the admin can see the raw template.
function resolveTokens(text, sample) {
  if (!text || !sample) return text || "";
  return text.replace(/\{\{\s*([a-zA-Z_][a-zA-Z0-9_]*)\s*\}\}/g, (m, k) => {
    if (Object.prototype.hasOwnProperty.call(sample, k)) return sample[k];
    return m;
  });
}

// ── Element renderer (preview) ─────────────────────────────────────
function ElementView({ el, selected, onMouseDown, sample }) {
  const style = {
    position: "absolute",
    left: el.x, top: el.y, width: el.w, height: el.h,
    zIndex: (el.z || 0) + (selected ? 100 : 0),
    cursor: selected ? "move" : "grab",
    outline: selected ? "2px solid #2563eb" : "none",
    outlineOffset: 2,
  };
  const commonProps = {
    style,
    onMouseDown,
    "data-testid": `cert-element-${el.id}`,
  };

  const fontFace = (AVAILABLE_FONTS.find(f => f.name === el.font_family) || AVAILABLE_FONTS[0]).css;

  if (el.type === "text") {
    return (
      <div {...commonProps} style={{
        ...style,
        fontFamily: fontFace,
        fontSize: el.font_size,
        fontWeight: el.font_weight,
        fontStyle: el.font_style,
        color: el.color,
        textAlign: el.align,
        display: "flex",
        alignItems: "center",
        justifyContent: el.align === "left" ? "flex-start" : el.align === "right" ? "flex-end" : "center",
        padding: 4,
        userSelect: "none",
      }}>
        <span style={{ width: "100%", lineHeight: 1.2, wordBreak: "break-word" }}>{resolveTokens(el.text, sample)}</span>
      </div>
    );
  }
  if (el.type === "placeholder") {
    const val = sample?.[el.placeholder_key] || `{{${el.placeholder_key}}}`;
    return (
      <div {...commonProps} style={{
        ...style,
        fontFamily: fontFace,
        fontSize: el.font_size,
        fontWeight: el.font_weight,
        color: el.color,
        textAlign: el.align,
        display: "flex",
        alignItems: "center",
        justifyContent: el.align === "left" ? "flex-start" : el.align === "right" ? "flex-end" : "center",
        padding: 4,
        userSelect: "none",
      }}>
        <span style={{ width: "100%", lineHeight: 1.1 }}>{val}</span>
      </div>
    );
  }
  if (el.type === "image" || el.type === "signature" || el.type === "seal" || el.type === "logo") {
    return (
      <div {...commonProps} style={{
        ...style,
        border: el.border_enabled ? `${el.border_width}px solid ${el.border_color}` : "none",
        opacity: el.opacity,
        background: !el.url ? "repeating-linear-gradient(45deg, #eef2f7, #eef2f7 8px, #dbe1ea 8px, #dbe1ea 16px)" : "transparent",
      }}>
        {el.url ? (
          <img src={el.url} alt={el.type} draggable={false}
               style={{ width: "100%", height: "100%", objectFit: "contain", pointerEvents: "none" }} />
        ) : (
          <div style={{ width: "100%", height: "100%", display: "flex", alignItems: "center", justifyContent: "center", color: "#94a3b8", fontSize: 12, textTransform: "capitalize" }}>
            {el.type}
          </div>
        )}
      </div>
    );
  }
  if (el.type === "shape") {
    return (
      <div {...commonProps} style={{
        ...style,
        background: el.fill_color,
        opacity: el.opacity,
        border: el.border_enabled ? `${el.border_width}px ${el.border_style || "solid"} ${el.border_color}` : "none",
      }} />
    );
  }
  if (el.type === "line") {
    return (
      <div {...commonProps} style={{
        ...style,
        background: el.line_color,
        height: el.thickness,
      }} />
    );
  }
  if (el.type === "qr") {
    return (
      <div {...commonProps} style={{
        ...style,
        display: "grid",
        gridTemplateColumns: "repeat(6, 1fr)",
        gridTemplateRows: "repeat(6, 1fr)",
        gap: 1,
        background: "white",
        padding: 4,
      }}>
        {Array.from({ length: 36 }).map((_, i) => (
          <div key={i} style={{ background: (i * 7) % 3 === 0 ? "#111827" : "#f3f4f6" }} />
        ))}
      </div>
    );
  }
  return null;
}

// ── Resize handles ────────────────────────────────────────────────
const HANDLE_DIRS = ["nw", "n", "ne", "e", "se", "s", "sw", "w"];
function ResizeHandles({ el, onStart }) {
  const positions = {
    nw: { left: 0, top: 0, cursor: "nwse-resize" },
    n:  { left: "50%", top: 0, cursor: "ns-resize", transform: "translateX(-50%)" },
    ne: { right: 0, top: 0, cursor: "nesw-resize" },
    e:  { right: 0, top: "50%", cursor: "ew-resize", transform: "translateY(-50%)" },
    se: { right: 0, bottom: 0, cursor: "nwse-resize" },
    s:  { left: "50%", bottom: 0, cursor: "ns-resize", transform: "translateX(-50%)" },
    sw: { left: 0, bottom: 0, cursor: "nesw-resize" },
    w:  { left: 0, top: "50%", cursor: "ew-resize", transform: "translateY(-50%)" },
  };
  return (
    <div style={{ position: "absolute", left: el.x - 4, top: el.y - 4, width: el.w + 8, height: el.h + 8, pointerEvents: "none", zIndex: 999 }}>
      {HANDLE_DIRS.map(dir => (
        <div
          key={dir}
          onMouseDown={(e) => { e.stopPropagation(); e.preventDefault(); onStart(dir, e); }}
          style={{
            position: "absolute",
            width: 10, height: 10,
            background: "#2563eb",
            border: "1px solid white",
            pointerEvents: "auto",
            ...positions[dir],
          }}
          data-testid={`cert-resize-handle-${dir}`}
        />
      ))}
    </div>
  );
}

// Build a synthetic "Verify at ..." text element positioned at the bottom
// centre of the canvas. Injected into the preview render list when the
// admin's design doesn't already include a `{{verification_url}}` token
// or a QR element — so the on-screen preview matches the backend PDF
// (which does the same auto-injection during rendering).
export function buildVerifyFooter(orientation, sampleUrl) {
  const W = orientation === "portrait" ? CANVAS_W_PORTRAIT : CANVAS_W_LANDSCAPE;
  const H = orientation === "portrait" ? CANVAS_H_PORTRAIT : CANVAS_H_LANDSCAPE;
  const footerW = Math.min(W - 40, 900);
  return {
    id: "__auto_verify_footer__",
    type: "text",
    __system: true,          // marks this as auto-injected (not persisted)
    x: (W - footerW) / 2,
    y: H - 45,
    w: footerW,
    h: 30,
    z: 9999,
    text: `Verify this certificate: ${sampleUrl}`,
    font_family: "Helvetica",
    font_size: 12,
    font_weight: "normal",
    font_style: "italic",
    color: "#787878",
    align: "center",
  };
}

// True when the design already contains a `{{verification_url}}` /
// `{{verify_url}}` token or a QR element — in which case the synthetic
// footer is suppressed to respect the admin's placement.
export function designHasVerifyToken(elements) {
  const keys = ["verification_url", "verify_url", "verify_qr_url"];
  for (const el of elements || []) {
    if (el.type === "qr") return true;
    const text = (el.text || "").toLowerCase();
    const pk = (el.placeholder_key || "").toLowerCase();
    for (const k of keys) {
      if (text.includes("{{" + k + "}}")) return true;
      if (pk === k) return true;
    }
  }
  return false;
}

// ── Full-page preview (paper-size, read-only) ─────────────────────
function FullPreview({ elements, background, orientation, sample, onClose }) {
  const CANVAS_W = orientation === "portrait" ? CANVAS_W_PORTRAIT : CANVAS_W_LANDSCAPE;
  const CANVAS_H = orientation === "portrait" ? CANVAS_H_PORTRAIT : CANVAS_H_LANDSCAPE;
  const [scale, setScale] = useState(0.5);

  // Fit the paper inside the viewport, leaving room for the header.
  useEffect(() => {
    const compute = () => {
      const availW = Math.max(320, window.innerWidth - 64);
      const availH = Math.max(320, window.innerHeight - 120);
      setScale(Math.min(availW / CANVAS_W, availH / CANVAS_H));
    };
    compute();
    window.addEventListener("resize", compute);
    return () => window.removeEventListener("resize", compute);
  }, [CANVAS_W, CANVAS_H]);

  // Esc to close.
  useEffect(() => {
    const onKey = (e) => { if (e.key === "Escape") onClose(); };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose]);

  const bg = background || {};
  return (
    <div
      className="fixed inset-0 z-[100] bg-slate-900 flex flex-col"
      onClick={onClose}
      data-testid="full-preview-modal"
    >
      <div className="flex justify-between items-center p-3 text-white">
        <div>
          <p className="text-[10px] uppercase tracking-widest text-slate-300 font-bold">Full Preview</p>
          <p className="text-sm text-white">A4 {orientation} · fit to screen · dynamic tokens resolved with sample data</p>
        </div>
        <button
          onClick={(e) => { e.stopPropagation(); onClose(); }}
          className="rounded-none border border-white/30 text-white px-3 py-1.5 text-xs hover:bg-white/10 flex items-center gap-1"
          data-testid="full-preview-close"
        ><X size={14} /> Close (Esc)</button>
      </div>
      <div className="flex-1 flex items-center justify-center overflow-auto p-4">
        <div
          onClick={(e) => e.stopPropagation()}
          style={{ width: CANVAS_W * scale, height: CANVAS_H * scale, position: "relative" }}
        >
          <div
            style={{
              width: CANVAS_W, height: CANVAS_H,
              background: bg.color || "white",
              position: "relative",
              transform: `scale(${scale})`,
              transformOrigin: "top left",
              boxShadow: "0 20px 60px rgba(0,0,0,0.5)",
              overflow: "hidden",
            }}
            data-testid="full-preview-canvas"
          >
            {bg.image_url && (
              <img src={bg.image_url} alt="bg" draggable={false}
                   style={{ position: "absolute", inset: 0, width: "100%", height: "100%",
                            objectFit: "cover", opacity: bg.opacity ?? 1,
                            pointerEvents: "none", userSelect: "none" }} />
            )}
            {bg.watermark_url && (
              <img src={bg.watermark_url} alt="wm" draggable={false}
                   style={{ position: "absolute", left: "50%", top: "50%",
                            transform: "translate(-50%, -50%)",
                            maxWidth: "60%", maxHeight: "60%",
                            opacity: bg.watermark_opacity ?? 0.15,
                            pointerEvents: "none", userSelect: "none" }} />
            )}
            {(designHasVerifyToken(elements)
              ? elements
              : [...elements, buildVerifyFooter(orientation, sample?.verification_url || "https://schoolbuilderlab.net.ng/verify/SBL-2026-SAMPLE")]
             ).slice().sort((a, b) => (a.z || 0) - (b.z || 0)).map(el => (
              <ElementView
                key={el.id}
                el={el}
                selected={false}
                onMouseDown={() => {}}
                sample={sample}
              />
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}

// ── The main editor ───────────────────────────────────────────────
export default function CertificateCanvasEditor({ initialElements, onChange, sample, orientation = "landscape", background, onBackgroundChange, onTemplateApply }) {
  useEffect(() => { loadGoogleFontsOnce(); }, []);
  const CANVAS_W = orientation === "portrait" ? CANVAS_W_PORTRAIT : CANVAS_W_LANDSCAPE;
  const CANVAS_H = orientation === "portrait" ? CANVAS_H_PORTRAIT : CANVAS_H_LANDSCAPE;
  const bg = background || {};
  const [decorOpen, setDecorOpen] = useState(false);
  const [showBgPanel, setShowBgPanel] = useState(false);
  const [templatesOpen, setTemplatesOpen] = useState(false);
  const [fieldsOpen, setFieldsOpen] = useState(false);
  const [previewTokens, setPreviewTokens] = useState(true);
  const [fullPreviewOpen, setFullPreviewOpen] = useState(false);

  const [elements, setElements] = useState(initialElements || []);
  const [selectedId, setSelectedId] = useState(null);
  const canvasRef = useRef(null);
  const [scale, setScale] = useState(1);
  const dragRef = useRef(null);   // { id, mode, startX, startY, orig }
  const hydratedRef = useRef(false);
  const isInternalUpdateRef = useRef(false);

  // ── Undo / Redo history (session-only) ─────────────────────────
  // Stack of { elements, background } snapshots. `pointer` points at the
  // current state. Undo -> pointer--, Redo -> pointer++.
  const HISTORY_LIMIT = 50;
  const historyRef = useRef([]);
  const pointerRef = useRef(-1);
  const suppressHistoryRef = useRef(false);
  const commitTimerRef = useRef(null);
  const elementsRef = useRef(initialElements || []);
  const [historyTick, setHistoryTick] = useState(0);   // triggers re-render for button disabled state
  const bgRef = useRef(bg);
  useEffect(() => { bgRef.current = bg; }, [bg]);

  const snapshotState = useCallback(() => ({
    elements: JSON.parse(JSON.stringify(elementsRef.current)),
    background: JSON.parse(JSON.stringify(bgRef.current || {})),
  }), []);

  const commitHistory = useCallback((snap) => {
    if (suppressHistoryRef.current) return;
    const s = snap || snapshotState();
    // Drop any future states if we branched off after an undo.
    const stack = historyRef.current.slice(0, pointerRef.current + 1);
    // Skip if identical to last committed snapshot.
    const last = stack[stack.length - 1];
    if (last && JSON.stringify(last) === JSON.stringify(s)) return;
    stack.push(s);
    // Trim from front if we exceed limit.
    while (stack.length > HISTORY_LIMIT) stack.shift();
    historyRef.current = stack;
    pointerRef.current = stack.length - 1;
    setHistoryTick(t => t + 1);
  }, [snapshotState]);

  // Commit the current state on the NEXT tick so React's batched state
  // update has flushed. Used by discrete UI actions (add / delete / etc.)
  // so undo can rewind to the exact state before the action.
  const commitSoon = useCallback(() => {
    if (commitTimerRef.current) { clearTimeout(commitTimerRef.current); commitTimerRef.current = null; }
    setTimeout(() => { commitHistory(); }, 0);
  }, [commitHistory]);

  const applySnapshot = useCallback((snap) => {
    // Cancel any pending debounced commit first — otherwise it would fire
    // just after we finish the time-travel and wipe out the redo stack.
    if (commitTimerRef.current) { clearTimeout(commitTimerRef.current); commitTimerRef.current = null; }
    suppressHistoryRef.current = true;
    setElements(snap.elements);
    if (onBackgroundChange) onBackgroundChange(snap.background || {});
    // Release suppression only AFTER the debounce window has fully passed
    // so React's post-setState effect can't accidentally re-commit.
    setTimeout(() => { suppressHistoryRef.current = false; }, 500);
  }, [onBackgroundChange]);

  const undo = useCallback(() => {
    if (pointerRef.current <= 0) return;
    pointerRef.current -= 1;
    applySnapshot(historyRef.current[pointerRef.current]);
    setHistoryTick(t => t + 1);
  }, [applySnapshot]);

  const redo = useCallback(() => {
    if (pointerRef.current >= historyRef.current.length - 1) return;
    pointerRef.current += 1;
    applySnapshot(historyRef.current[pointerRef.current]);
    setHistoryTick(t => t + 1);
  }, [applySnapshot]);

  const canUndo = pointerRef.current > 0;
  const canRedo = pointerRef.current < historyRef.current.length - 1;

  // Keyboard shortcuts: Ctrl/Cmd+Z (undo), Ctrl/Cmd+Shift+Z / Ctrl+Y (redo).
  useEffect(() => {
    const isEditableTarget = (t) => {
      if (!t) return false;
      const tag = (t.tagName || "").toLowerCase();
      return tag === "input" || tag === "textarea" || t.isContentEditable;
    };
    const onKey = (e) => {
      if (isEditableTarget(e.target)) return;   // don't hijack while user is typing
      const mod = e.ctrlKey || e.metaKey;
      if (!mod) return;
      const key = (e.key || "").toLowerCase();
      if (key === "z" && !e.shiftKey) { e.preventDefault(); undo(); }
      else if ((key === "z" && e.shiftKey) || key === "y") { e.preventDefault(); redo(); }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [undo, redo]);

  // Sync back to parent (skip while we're hydrating from parent).
  useEffect(() => {
    elementsRef.current = elements;
    if (!hydratedRef.current) { hydratedRef.current = true; return; }
    if (isInternalUpdateRef.current) return;
    onChange && onChange(elements);
  }, [elements]); // eslint-disable-line react-hooks/exhaustive-deps

  // Only hydrate from parent when the identity actually changes and the
  // change did NOT originate here (e.g. loading a saved version).
  const lastHydrateRef = useRef("init");
  useEffect(() => {
    const incoming = initialElements || [];
    // Cheap identity + length check — full JSON compare avoids the infinite loop.
    if (JSON.stringify(incoming) === JSON.stringify(elements)) { lastHydrateRef.current = "skip-equal"; return; }
    // Ignore parent hydration attempts that arrive shortly after an undo/redo
    // (parent's state hasn't caught up yet with the local history-driven state).
    if (suppressHistoryRef.current) { lastHydrateRef.current = "skip-suppressed"; return; }
    lastHydrateRef.current = "applied-reset";
    isInternalUpdateRef.current = true;
    setElements(incoming);
    // Reset history to the newly-loaded baseline so undo can't step across designs.
    historyRef.current = [{
      elements: JSON.parse(JSON.stringify(incoming)),
      background: JSON.parse(JSON.stringify(bgRef.current || {})),
    }];
    pointerRef.current = 0;
    setHistoryTick(t => t + 1);
    // Release the flag after React flushes the update.
    setTimeout(() => { isInternalUpdateRef.current = false; }, 0);
  }, [initialElements]); // eslint-disable-line react-hooks/exhaustive-deps

  // Debounced auto-commit whenever elements or background change.
  useEffect(() => {
    if (suppressHistoryRef.current) return;
    if (commitTimerRef.current) clearTimeout(commitTimerRef.current);
    commitTimerRef.current = setTimeout(() => { commitHistory(); }, 400);
    return () => { if (commitTimerRef.current) clearTimeout(commitTimerRef.current); };
  }, [elements, bg.color, bg.image_url, bg.opacity, bg.watermark_url, bg.watermark_opacity, commitHistory]);

  // Seed the history with the initial state on mount, so the very first
  // undo can restore the design as it was loaded (not the state AFTER the
  // admin's first action).
  useEffect(() => {
    if (historyRef.current.length === 0) {
      historyRef.current = [{
        elements: JSON.parse(JSON.stringify(elements)),
        background: JSON.parse(JSON.stringify(bgRef.current || {})),
      }];
      pointerRef.current = 0;
      setHistoryTick(t => t + 1);
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Compute display scale so canvas fits the viewport width.
  useEffect(() => {
    const compute = () => {
      const parent = canvasRef.current?.parentElement;
      if (!parent) return;
      const w = parent.clientWidth - 32;
      setScale(Math.min(1, w / CANVAS_W));
    };
    compute();
    window.addEventListener("resize", compute);
    return () => window.removeEventListener("resize", compute);
  }, [CANVAS_W]);

  const selected = elements.find(e => e.id === selectedId) || null;

  const update = (id, patch) =>
    setElements(prev => prev.map(e => (e.id === id ? { ...e, ...patch } : e)));

  const addElement = (factory) => {
    const el = factory();
    setElements(prev => [...prev, el]);
    setSelectedId(el.id);
    commitSoon();
  };

  // Insert a text element whose content is a dynamic placeholder token
  // (e.g. "{{student_name}}"). The token is preserved verbatim in the
  // saved design; the backend PDF generator resolves it at issue time.
  const insertDynamicField = (fieldKey) => {
    const field = DYNAMIC_FIELDS.find(f => f.key === fieldKey);
    if (!field) return;
    const el = {
      ...newTextEl(300, 300),
      text: `{{${fieldKey}}}`,
      dynamic_field: fieldKey,
      w: Math.max(320, field.label.length * 14),
      h: 60,
    };
    setElements(prev => [...prev, el]);
    setSelectedId(el.id);
    setFieldsOpen(false);
    commitSoon();
  };

  const deleteSelected = () => {
    if (!selectedId) return;
    setElements(prev => prev.filter(e => e.id !== selectedId));
    setSelectedId(null);
    commitSoon();
  };

  const duplicateSelected = () => {
    if (!selected) return;
    const clone = { ...selected, id: uid(), x: selected.x + 24, y: selected.y + 24 };
    setElements(prev => [...prev, clone]);
    setSelectedId(clone.id);
    commitSoon();
  };

  const bringToFront = () => {
    if (!selected) return;
    const maxZ = Math.max(0, ...elements.map(e => e.z || 0));
    update(selected.id, { z: maxZ + 1 });
    commitSoon();
  };
  const sendToBack = () => {
    if (!selected) return;
    const minZ = Math.min(0, ...elements.map(e => e.z || 0));
    update(selected.id, { z: minZ - 1 });
    commitSoon();
  };

  // ── Drag handlers ─────────────────────────────────────────────
  const onElementMouseDown = (e, el) => {
    e.stopPropagation();
    setSelectedId(el.id);
    dragRef.current = {
      id: el.id, mode: "move",
      startX: e.clientX, startY: e.clientY,
      orig: { x: el.x, y: el.y, w: el.w, h: el.h },
    };
  };

  const onResizeStart = (dir, e) => {
    if (!selected) return;
    dragRef.current = {
      id: selected.id, mode: "resize", dir,
      startX: e.clientX, startY: e.clientY,
      orig: { x: selected.x, y: selected.y, w: selected.w, h: selected.h },
    };
  };

  const onMouseMove = useCallback((e) => {
    const st = dragRef.current;
    if (!st) return;
    const dx = (e.clientX - st.startX) / scale;
    const dy = (e.clientY - st.startY) / scale;
    if (st.mode === "move") {
      const nx = clamp(st.orig.x + dx, 0, CANVAS_W - st.orig.w);
      const ny = clamp(st.orig.y + dy, 0, CANVAS_H - st.orig.h);
      update(st.id, { x: nx, y: ny });
    } else if (st.mode === "resize") {
      let { x, y, w, h } = st.orig;
      const dir = st.dir;
      if (dir.includes("e")) w = clamp(st.orig.w + dx, 20, CANVAS_W - x);
      if (dir.includes("s")) h = clamp(st.orig.h + dy, 10, CANVAS_H - y);
      if (dir.includes("w")) { const nx = clamp(st.orig.x + dx, 0, st.orig.x + st.orig.w - 20); w = st.orig.w + (st.orig.x - nx); x = nx; }
      if (dir.includes("n")) { const ny = clamp(st.orig.y + dy, 0, st.orig.y + st.orig.h - 10); h = st.orig.h + (st.orig.y - ny); y = ny; }
      update(st.id, { x, y, w, h });
    }
  }, [scale]); // eslint-disable-line react-hooks/exhaustive-deps

  const onMouseUp = () => {
    if (dragRef.current) {
      dragRef.current = null;
      commitSoon();     // snapshot the final position/size after drag or resize
    }
  };

  useEffect(() => {
    window.addEventListener("mousemove", onMouseMove);
    window.addEventListener("mouseup", onMouseUp);
    return () => {
      window.removeEventListener("mousemove", onMouseMove);
      window.removeEventListener("mouseup", onMouseUp);
    };
  }, [onMouseMove]);

  // ── Image upload for selected element ─────────────────────────
  const fileRef = useRef(null);
  const onUpload = async (e) => {
    const f = e.target.files?.[0];
    if (!f || !selected) return;
    if (f.size > 5 * 1024 * 1024) return toast.error("Max 5MB");
    try {
      const fd = new FormData();
      fd.append("file", f);
      const { data } = await api.post("/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      const url = (process.env.REACT_APP_BACKEND_URL || "") + data.url;
      update(selected.id, { url });
      toast.success("Uploaded");
    } catch { toast.error("Upload failed"); }
    e.target.value = "";
  };

  const hasImage = selected && ["image", "signature", "seal", "logo"].includes(selected.type);
  const hasText = selected && ["text", "placeholder"].includes(selected.type);

  return (
    <div className="grid grid-cols-1 md:grid-cols-[minmax(0,1fr)_260px] gap-3" data-testid="cert-canvas-editor">
      <div className="min-w-0">
        {/* Toolbar */}
        <div className="flex flex-wrap gap-1.5 mb-3 p-2 bg-slate-50 border border-slate-200" data-testid="cert-toolbar">
          <Button
            size="sm" variant="outline" onClick={undo} disabled={!canUndo}
            className="rounded-none gap-1 text-xs disabled:opacity-40"
            title="Undo (Ctrl+Z)"
            data-testid="undo-btn"
          ><ArrowUUpLeft size={14} /> Undo</Button>
          <Button
            size="sm" variant="outline" onClick={redo} disabled={!canRedo}
            className="rounded-none gap-1 text-xs disabled:opacity-40"
            title="Redo (Ctrl+Shift+Z)"
            data-testid="redo-btn"
          ><ArrowUUpRight size={14} /> Redo</Button>
          <Button
            size="sm" variant="outline"
            onClick={() => setPreviewTokens(v => !v)}
            className={`rounded-none gap-1 text-xs ${previewTokens ? "bg-slate-100" : "bg-yellow-50 border-yellow-300 text-yellow-800"}`}
            title={previewTokens ? "Showing sample data (click to show raw tokens)" : "Showing raw tokens (click to preview data)"}
            data-testid="preview-toggle-btn"
          >
            {previewTokens ? <Eye size={14} /> : <EyeSlash size={14} />}
            {previewTokens ? "Preview" : "Tokens"}
          </Button>
          <Button
            size="sm" variant="outline"
            onClick={() => setFullPreviewOpen(true)}
            className="rounded-none gap-1 text-xs bg-violet-50 border-violet-300 text-violet-800 hover:bg-violet-100"
            title="See the whole certificate at paper size (A4) fit to your screen"
            data-testid="full-preview-btn"
          ><ArrowsOut size={14} /> Full Preview</Button>
          <span className="w-px bg-slate-300 mx-1" />
          <Button size="sm" variant="outline" onClick={() => setTemplatesOpen(true)} className="rounded-none gap-1 text-xs bg-emerald-50 border-emerald-300 text-emerald-800 hover:bg-emerald-100" data-testid="templates-btn"><SealIcon size={14} /> Templates</Button>
          <Button size="sm" variant="outline" onClick={() => setShowBgPanel(v => !v)} className={`rounded-none gap-1 text-xs ${showBgPanel ? "bg-blue-100 border-blue-400" : ""}`} data-testid="bg-btn"><Square size={14} /> Background</Button>
          <Button size="sm" variant="outline" onClick={() => setDecorOpen(true)} className="rounded-none gap-1 text-xs bg-amber-50 border-amber-300 text-amber-800 hover:bg-amber-100" data-testid="add-decor-btn"><SealIcon size={14} /> Decorations</Button>
          <div className="relative">
            <Button
              size="sm" variant="outline"
              onClick={() => setFieldsOpen(v => !v)}
              className={`rounded-none gap-1 text-xs bg-indigo-50 border-indigo-300 text-indigo-800 hover:bg-indigo-100 ${fieldsOpen ? "ring-2 ring-indigo-400" : ""}`}
              data-testid="dynamic-fields-btn"
            ><Sparkle size={14} /> Dynamic Field ▾</Button>
            {fieldsOpen && (
              <>
                <div className="fixed inset-0 z-30" onClick={() => setFieldsOpen(false)} />
                <div
                  className="absolute left-0 top-full mt-1 z-40 bg-white border border-slate-300 shadow-lg w-64 max-h-80 overflow-y-auto"
                  data-testid="dynamic-fields-menu"
                >
                  <div className="p-2 border-b bg-indigo-50 text-[10px] uppercase tracking-widest text-indigo-800 font-bold">
                    Insert Dynamic Field
                  </div>
                  {DYNAMIC_FIELDS.map(f => (
                    <button
                      key={f.key}
                      type="button"
                      onClick={() => insertDynamicField(f.key)}
                      className="w-full text-left px-3 py-2 hover:bg-indigo-50 border-b border-slate-100 last:border-b-0"
                      data-testid={`dynamic-field-${f.key}`}
                    >
                      <div className="text-xs font-semibold text-slate-900">{f.label}</div>
                      <div className="text-[10px] text-slate-500 font-mono">{`{{${f.key}}}`}</div>
                    </button>
                  ))}
                </div>
              </>
            )}
          </div>
          <span className="w-px bg-slate-300 mx-1" />
          <Button size="sm" variant="outline" onClick={() => addElement(newTextEl)} className="rounded-none gap-1 text-xs" data-testid="add-text-btn"><TextT size={14} /> Text</Button>
          <Button size="sm" variant="outline" onClick={() => addElement(() => newImageEl())} className="rounded-none gap-1 text-xs" data-testid="add-image-btn"><ImageIcon size={14} /> Image</Button>
          <Button size="sm" variant="outline" onClick={() => addElement(newLogoEl)} className="rounded-none gap-1 text-xs" data-testid="add-logo-btn"><ImageIcon size={14} /> Logo</Button>
          <Button size="sm" variant="outline" onClick={() => addElement(newShapeEl)} className="rounded-none gap-1 text-xs" data-testid="add-shape-btn"><Square size={14} /> Shape</Button>
          <Button size="sm" variant="outline" onClick={() => addElement(newLineEl)} className="rounded-none gap-1 text-xs" data-testid="add-line-btn"><LineSegment size={14} /> Line</Button>
          <Button size="sm" variant="outline" onClick={() => addElement(newSignatureEl)} className="rounded-none gap-1 text-xs" data-testid="add-sig-btn"><SignatureIcon size={14} /> Signature</Button>
          <Button size="sm" variant="outline" onClick={() => addElement(newSealEl)} className="rounded-none gap-1 text-xs" data-testid="add-seal-btn"><SealIcon size={14} /> Seal</Button>
          <Button size="sm" variant="outline" onClick={() => addElement(() => newPlaceholderEl())} className="rounded-none gap-1 text-xs" data-testid="add-placeholder-btn"><Plus size={14} /> Placeholder</Button>
          <Button size="sm" variant="outline" onClick={() => addElement(newQrEl)} className="rounded-none gap-1 text-xs" data-testid="add-qr-btn"><QrCode size={14} /> QR</Button>
        </div>

        {/* Background properties panel */}
        {showBgPanel && (
          <div className="mb-3 p-3 border border-slate-200 bg-white grid grid-cols-1 sm:grid-cols-3 gap-3" data-testid="bg-panel">
            <div>
              <Label className="text-[10px]">Background colour</Label>
              <input type="color" value={bg.color || "#ffffff"} onChange={e => onBackgroundChange && onBackgroundChange({ ...bg, color: e.target.value })} className="w-full h-9 border border-slate-200 rounded-none" data-testid="bg-color-input" />
            </div>
            <div>
              <Label className="text-[10px]">Background image URL / upload</Label>
              <Input value={bg.image_url || ""} onChange={e => onBackgroundChange && onBackgroundChange({ ...bg, image_url: e.target.value })} placeholder="https://…" className="rounded-none h-8 text-xs" data-testid="bg-url-input" />
              <input type="file" accept="image/*" className="hidden" id="bg-file-input" onChange={async e => {
                const f = e.target.files?.[0]; if (!f) return;
                if (f.size > 5 * 1024 * 1024) return toast.error("Max 5MB");
                const fd = new FormData(); fd.append("file", f);
                try {
                  const { data } = await api.post("/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
                  const url = (process.env.REACT_APP_BACKEND_URL || "") + data.url;
                  onBackgroundChange && onBackgroundChange({ ...bg, image_url: url });
                  toast.success("Background uploaded");
                } catch { toast.error("Upload failed"); }
                e.target.value = "";
              }} />
              <Button size="sm" variant="outline" onClick={() => document.getElementById("bg-file-input").click()} className="rounded-none text-xs h-7 mt-1 gap-1" data-testid="bg-upload-btn"><UploadSimple size={12} /> Upload</Button>
            </div>
            <div>
              <Label className="text-[10px]">Image brightness / opacity ({Math.round((bg.opacity ?? 1) * 100)}%)</Label>
              <input type="range" min={0.05} max={1} step={0.05} value={bg.opacity ?? 1} onChange={e => onBackgroundChange && onBackgroundChange({ ...bg, opacity: Number(e.target.value) })} className="w-full" data-testid="bg-opacity-slider" />
              {bg.image_url && (
                <Button size="sm" variant="outline" onClick={() => onBackgroundChange && onBackgroundChange({ ...bg, image_url: "" })} className="rounded-none text-xs h-7 mt-1 gap-1 border-red-300 text-red-700" data-testid="bg-remove-btn"><Trash size={12} /> Remove image</Button>
              )}
            </div>
            <div className="sm:col-span-3 border-t pt-3 mt-1">
              <Label className="text-[10px]">Watermark (subtle overlay behind everything)</Label>
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-2 mt-1">
                <Input value={bg.watermark_url || ""} onChange={e => onBackgroundChange && onBackgroundChange({ ...bg, watermark_url: e.target.value })} placeholder="Watermark image URL" className="rounded-none h-8 text-xs" data-testid="wm-url-input" />
                <div>
                  <input type="file" accept="image/*" className="hidden" id="wm-file-input" onChange={async e => {
                    const f = e.target.files?.[0]; if (!f) return;
                    if (f.size > 5 * 1024 * 1024) return toast.error("Max 5MB");
                    const fd = new FormData(); fd.append("file", f);
                    try {
                      const { data } = await api.post("/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
                      const url = (process.env.REACT_APP_BACKEND_URL || "") + data.url;
                      onBackgroundChange && onBackgroundChange({ ...bg, watermark_url: url });
                      toast.success("Watermark uploaded");
                    } catch { toast.error("Upload failed"); }
                    e.target.value = "";
                  }} />
                  <Button size="sm" variant="outline" onClick={() => document.getElementById("wm-file-input").click()} className="rounded-none text-xs h-8 gap-1 w-full" data-testid="wm-upload-btn"><UploadSimple size={12} /> Upload watermark</Button>
                </div>
                <div>
                  <Label className="text-[10px]">Watermark opacity ({Math.round((bg.watermark_opacity ?? 0.15) * 100)}%)</Label>
                  <input type="range" min={0.02} max={0.6} step={0.02} value={bg.watermark_opacity ?? 0.15} onChange={e => onBackgroundChange && onBackgroundChange({ ...bg, watermark_opacity: Number(e.target.value) })} className="w-full" data-testid="wm-opacity-slider" />
                </div>
              </div>
              {bg.watermark_url && (
                <Button size="sm" variant="outline" onClick={() => onBackgroundChange && onBackgroundChange({ ...bg, watermark_url: "" })} className="rounded-none text-xs h-7 mt-1 gap-1 border-red-300 text-red-700" data-testid="wm-remove-btn"><Trash size={12} /> Remove watermark</Button>
              )}
            </div>
          </div>
        )}

        {/* Templates gallery */}
        {templatesOpen && (
          <div className="fixed inset-0 bg-black/40 z-40 flex items-center justify-center p-4" onClick={() => setTemplatesOpen(false)} data-testid="templates-drawer">
            <div onClick={e => e.stopPropagation()} className="bg-white max-w-3xl w-full max-h-[80vh] overflow-y-auto p-4 border border-slate-200">
              <div className="flex justify-between items-center mb-3">
                <h3 className="text-lg font-bold" style={{ fontFamily: "'Outfit', sans-serif" }}>Sample Templates</h3>
                <button onClick={() => setTemplatesOpen(false)} className="text-slate-500 text-xl leading-none" data-testid="templates-close">×</button>
              </div>
              <p className="text-xs text-slate-500 mb-3">Pick a starter design — it will replace the current canvas. You can then move, edit, resize, or delete any part of it.</p>
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                {CANVAS_TEMPLATES.map(t => (
                  <button
                    key={t.id}
                    onClick={() => {
                      onTemplateApply && onTemplateApply(t);
                      setTemplatesOpen(false);
                      toast.success(`Applied: ${t.label}`);
                      commitSoon();
                    }}
                    className="border border-slate-200 hover:border-blue-500 hover:bg-blue-50 p-3 text-left"
                    data-testid={`template-${t.id}`}
                  >
                    <p className="text-sm font-semibold text-slate-900">{t.label}</p>
                    <p className="text-[10px] text-slate-500 uppercase tracking-widest mt-0.5">{t.orientation}</p>
                    <p className="text-[10px] text-slate-500 mt-1">{t.build().length} elements pre-arranged</p>
                  </button>
                ))}
              </div>
            </div>
          </div>
        )}

        {/* Decorations drawer */}
        {decorOpen && (
          <div className="fixed inset-0 bg-black/40 z-40 flex items-center justify-center p-4" onClick={() => setDecorOpen(false)} data-testid="decor-drawer">
            <div onClick={e => e.stopPropagation()} className="bg-white max-w-3xl w-full max-h-[80vh] overflow-y-auto p-4 border border-slate-200">
              <div className="flex justify-between items-center mb-3">
                <h3 className="text-lg font-bold" style={{ fontFamily: "'Outfit', sans-serif" }}>Decorative Art Library</h3>
                <button onClick={() => setDecorOpen(false)} className="text-slate-500 text-xl leading-none" data-testid="decor-close">×</button>
              </div>
              <p className="text-xs text-slate-500 mb-3">Click any decoration to drop it onto the canvas. You can then move, resize, adjust opacity or delete it just like any other object.</p>
              <div className="grid grid-cols-3 sm:grid-cols-4 lg:grid-cols-5 gap-2">
                {DECORATION_ITEMS.map(item => (
                  <button
                    key={item.id}
                    onClick={() => {
                      const el = { ...newImageEl(svgToDataUrl(item.svg), 300, 300), w: 200, h: 200, decor_id: item.id };
                      setElements(prev => [...prev, el]);
                      setSelectedId(el.id);
                      setDecorOpen(false);
                      commitSoon();
                    }}
                    className="border border-slate-200 hover:border-blue-500 hover:bg-blue-50 p-2 aspect-square flex flex-col items-center justify-center gap-1"
                    data-testid={`decor-${item.id}`}
                  >
                    <div className="flex-1 w-full flex items-center justify-center" dangerouslySetInnerHTML={{ __html: item.svg }} style={{ maxHeight: 80 }} />
                    <span className="text-[10px] text-slate-600 text-center">{item.label}</span>
                  </button>
                ))}
              </div>
            </div>
          </div>
        )}

        {/* Full Preview modal — shows the whole design at paper aspect ratio,
             fit to the viewport, with all dynamic tokens resolved. Read-only.
             Press Esc or click the backdrop to dismiss. */}
        {fullPreviewOpen && (
          <FullPreview
            elements={elements}
            background={bg}
            orientation={orientation}
            sample={{ ...DYNAMIC_SAMPLES, ...(sample || {}) }}
            onClose={() => setFullPreviewOpen(false)}
          />
        )}

        {/* Canvas frame */}
        <div className="overflow-auto bg-slate-100 p-4 border border-slate-200" style={{ maxHeight: "78vh" }}>
          <div style={{ width: CANVAS_W * scale, height: CANVAS_H * scale, position: "relative" }}>
            <div
              ref={canvasRef}
              onMouseDown={() => setSelectedId(null)}
              style={{
                width: CANVAS_W, height: CANVAS_H,
                background: bg.color || "white",
                position: "relative",
                transform: `scale(${scale})`,
                transformOrigin: "top left",
                boxShadow: "0 6px 24px rgba(0,0,0,0.12)",
                overflow: "hidden",
              }}
              data-testid="cert-canvas"
            >
              {bg.image_url && (
                <img
                  src={bg.image_url}
                  alt="canvas-bg"
                  draggable={false}
                  style={{
                    position: "absolute", inset: 0, width: "100%", height: "100%",
                    objectFit: "cover", opacity: bg.opacity ?? 1,
                    pointerEvents: "none", userSelect: "none",
                  }}
                  data-testid="cert-canvas-bg"
                />
              )}
              {bg.watermark_url && (
                <img
                  src={bg.watermark_url}
                  alt="canvas-watermark"
                  draggable={false}
                  style={{
                    position: "absolute", left: "50%", top: "50%",
                    transform: "translate(-50%, -50%)",
                    maxWidth: "60%", maxHeight: "60%",
                    opacity: bg.watermark_opacity ?? 0.15,
                    pointerEvents: "none", userSelect: "none",
                  }}
                  data-testid="cert-canvas-watermark"
                />
              )}
              {(() => {
                const previewSample = previewTokens ? { ...DYNAMIC_SAMPLES, ...(sample || {}) } : null;
                const renderList = designHasVerifyToken(elements)
                  ? elements
                  : [...elements, buildVerifyFooter(orientation, "https://schoolbuilderlab.net.ng/verify/SBL-2026-SAMPLE")];
                return renderList.slice().sort((a, b) => (a.z || 0) - (b.z || 0)).map(el => (
                  <ElementView
                    key={el.id}
                    el={el}
                    selected={el.id === selectedId}
                    onMouseDown={(e) => el.__system ? null : onElementMouseDown(e, el)}
                    sample={previewSample}
                  />
                ));
              })()}
              {selected && <ResizeHandles el={selected} onStart={onResizeStart} />}
            </div>
          </div>
        </div>
        <p className="text-[10px] text-slate-500 mt-1">Canvas: A4 {orientation} · {CANVAS_W}×{CANVAS_H}px · scale {Math.round(scale * 100)}%. Click any element to edit.</p>
      </div>

      {/* Property panel */}
      <div className="border border-slate-200 bg-white p-2.5 space-y-2.5 sticky top-4 self-start max-h-[86vh] overflow-y-auto min-w-0" data-testid="cert-props-panel">
        <div className="flex items-center justify-between">
          <p className="text-xs uppercase tracking-widest text-slate-500 font-bold">Properties</p>
          {selected && (
            <div className="flex gap-1">
              <button onClick={duplicateSelected} className="p-1 hover:bg-slate-100" title="Duplicate" data-testid="duplicate-el-btn"><ArrowClockwise size={14} /></button>
              <button onClick={deleteSelected} className="p-1 hover:bg-red-50 text-red-700" title="Delete" data-testid="delete-el-btn"><Trash size={14} /></button>
            </div>
          )}
        </div>
        {!selected && (
          <p className="text-xs text-slate-400">Select an element on the canvas, or add one from the toolbar.</p>
        )}
        {selected && (
          <>
            <div className="text-[10px] uppercase tracking-widest text-slate-400 border-b pb-1">{selected.type}</div>

            {/* Position + size (numeric) */}
            <div className="grid grid-cols-2 gap-2">
              <div><Label className="text-[10px]">X</Label><Input type="number" value={Math.round(selected.x)} onChange={e => update(selected.id, { x: Number(e.target.value) })} className="h-7 rounded-none text-xs" /></div>
              <div><Label className="text-[10px]">Y</Label><Input type="number" value={Math.round(selected.y)} onChange={e => update(selected.id, { y: Number(e.target.value) })} className="h-7 rounded-none text-xs" /></div>
              <div><Label className="text-[10px]">W</Label><Input type="number" value={Math.round(selected.w)} onChange={e => update(selected.id, { w: Number(e.target.value) })} className="h-7 rounded-none text-xs" /></div>
              <div><Label className="text-[10px]">H</Label><Input type="number" value={Math.round(selected.h)} onChange={e => update(selected.id, { h: Number(e.target.value) })} className="h-7 rounded-none text-xs" /></div>
            </div>

            {/* Layer */}
            <div className="flex gap-1">
              <Button size="sm" variant="outline" onClick={bringToFront} className="rounded-none text-[10px] flex-1 h-7" data-testid="bring-front-btn">Bring to front</Button>
              <Button size="sm" variant="outline" onClick={sendToBack} className="rounded-none text-[10px] flex-1 h-7" data-testid="send-back-btn">Send to back</Button>
            </div>

            {/* Text-specific */}
            {selected.type === "text" && (
              <>
                <div><Label className="text-[10px]">Text</Label><Textarea value={selected.text} onChange={e => update(selected.id, { text: e.target.value })} rows={3} className="rounded-none text-sm" data-testid="text-content-input" /></div>
              </>
            )}
            {selected.type === "placeholder" && (
              <div>
                <Label className="text-[10px]">Placeholder</Label>
                <Select value={selected.placeholder_key} onValueChange={v => update(selected.id, { placeholder_key: v })}>
                  <SelectTrigger className="rounded-none h-8 text-xs" data-testid="placeholder-key-select"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {PLACEHOLDER_KEYS.map(k => <SelectItem key={k} value={k}>{`{{${k}}}`}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
            )}
            {hasText && (
              <>
                <div>
                  <Label className="text-[10px]">Font</Label>
                  <Select value={selected.font_family} onValueChange={v => update(selected.id, { font_family: v })}>
                    <SelectTrigger className="rounded-none h-8 text-xs" data-testid="font-family-select"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      {AVAILABLE_FONTS.map(f => (
                        <SelectItem key={f.name} value={f.name}>
                          <span style={{ fontFamily: f.css }}>{f.name}</span>
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="grid grid-cols-2 gap-2">
                  <div><Label className="text-[10px]">Size (px)</Label><Input type="number" min={6} max={200} value={selected.font_size} onChange={e => update(selected.id, { font_size: Number(e.target.value) })} className="h-7 rounded-none text-xs" data-testid="font-size-input" /></div>
                  <div>
                    <Label className="text-[10px]">Colour</Label>
                    <input type="color" value={selected.color} onChange={e => update(selected.id, { color: e.target.value })} className="w-full h-7 border border-slate-200 rounded-none" data-testid="text-color-input" />
                  </div>
                </div>
                <div className="grid grid-cols-3 gap-1">
                  <Button size="sm" variant={selected.font_weight === "bold" ? "default" : "outline"} onClick={() => update(selected.id, { font_weight: selected.font_weight === "bold" ? "normal" : "bold" })} className="rounded-none text-xs h-7" data-testid="text-bold-btn"><b>B</b></Button>
                  <Button size="sm" variant={selected.font_style === "italic" ? "default" : "outline"} onClick={() => update(selected.id, { font_style: selected.font_style === "italic" ? "normal" : "italic" })} className="rounded-none text-xs h-7" data-testid="text-italic-btn"><i>I</i></Button>
                  <Select value={selected.align} onValueChange={v => update(selected.id, { align: v })}>
                    <SelectTrigger className="rounded-none h-7 text-[10px]" data-testid="text-align-select"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="left">Left</SelectItem>
                      <SelectItem value="center">Center</SelectItem>
                      <SelectItem value="right">Right</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </>
            )}

            {/* Image / signature / seal / logo */}
            {hasImage && (
              <>
                <div>
                  <Label className="text-[10px]">Image URL</Label>
                  <Input value={selected.url || ""} onChange={e => update(selected.id, { url: e.target.value })} className="h-7 rounded-none text-xs" placeholder="Paste URL or upload" data-testid="image-url-input" />
                  <div className="flex gap-2 mt-1">
                    <input type="file" accept="image/*" ref={fileRef} className="hidden" onChange={onUpload} />
                    <Button size="sm" variant="outline" onClick={() => fileRef.current?.click()} className="rounded-none text-xs h-7 gap-1" data-testid="upload-image-btn"><UploadSimple size={12} /> Upload</Button>
                  </div>
                </div>
                <div>
                  <Label className="text-[10px]">Brightness / Opacity ({Math.round((selected.opacity ?? 1) * 100)}%)</Label>
                  <input type="range" min={0.05} max={1} step={0.05} value={selected.opacity ?? 1} onChange={e => update(selected.id, { opacity: Number(e.target.value) })} className="w-full" data-testid="opacity-slider" />
                </div>
                <label className="flex items-center gap-2 text-xs cursor-pointer" data-testid="image-border-toggle">
                  <input type="checkbox" checked={!!selected.border_enabled} onChange={e => update(selected.id, { border_enabled: e.target.checked })} />
                  Show border
                </label>
                {selected.border_enabled && (
                  <div className="grid grid-cols-2 gap-2">
                    <div><Label className="text-[10px]">Border width</Label><Input type="number" min={0} max={20} value={selected.border_width} onChange={e => update(selected.id, { border_width: Number(e.target.value) })} className="h-7 rounded-none text-xs" /></div>
                    <div><Label className="text-[10px]">Border colour</Label><input type="color" value={selected.border_color} onChange={e => update(selected.id, { border_color: e.target.value })} className="w-full h-7 border border-slate-200 rounded-none" /></div>
                  </div>
                )}
              </>
            )}

            {/* Shape */}
            {selected.type === "shape" && (
              <>
                <div className="grid grid-cols-2 gap-2">
                  <div><Label className="text-[10px]">Fill</Label><input type="color" value={selected.fill_color} onChange={e => update(selected.id, { fill_color: e.target.value })} className="w-full h-7 border border-slate-200 rounded-none" data-testid="shape-fill-color" /></div>
                  <div>
                    <Label className="text-[10px]">Opacity ({Math.round((selected.opacity ?? 1) * 100)}%)</Label>
                    <input type="range" min={0} max={1} step={0.05} value={selected.opacity ?? 1} onChange={e => update(selected.id, { opacity: Number(e.target.value) })} className="w-full" />
                  </div>
                </div>
                <label className="flex items-center gap-2 text-xs cursor-pointer" data-testid="shape-border-toggle">
                  <input type="checkbox" checked={!!selected.border_enabled} onChange={e => update(selected.id, { border_enabled: e.target.checked })} />
                  Show border
                </label>
                {selected.border_enabled && (
                  <div className="grid grid-cols-2 gap-2">
                    <div><Label className="text-[10px]">Border width</Label><Input type="number" min={0} max={20} value={selected.border_width} onChange={e => update(selected.id, { border_width: Number(e.target.value) })} className="h-7 rounded-none text-xs" /></div>
                    <div><Label className="text-[10px]">Border colour</Label><input type="color" value={selected.border_color} onChange={e => update(selected.id, { border_color: e.target.value })} className="w-full h-7 border border-slate-200 rounded-none" /></div>
                  </div>
                )}
              </>
            )}

            {/* Line */}
            {selected.type === "line" && (
              <div className="grid grid-cols-2 gap-2">
                <div><Label className="text-[10px]">Colour</Label><input type="color" value={selected.line_color} onChange={e => update(selected.id, { line_color: e.target.value })} className="w-full h-7 border border-slate-200 rounded-none" data-testid="line-color-input" /></div>
                <div><Label className="text-[10px]">Thickness</Label><Input type="number" min={1} max={40} value={selected.thickness} onChange={e => update(selected.id, { thickness: Number(e.target.value), h: Number(e.target.value) })} className="h-7 rounded-none text-xs" /></div>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
}
